import React from 'react';
import { resolveSendDisabledState } from './composerShared.jsx';

const SUBMIT_BUTTON_SIZE = 38;

function resolveActionStyleValue(isStop, disabled, values) {
  if (isStop) return values.stop;
  if (disabled) return values.disabled;
  return values.active;
}

export function resolveComposerSubmitButtonStyle({
  action = 'send',
  disabled = false,
} = {}) {
  const isStop = action === 'stop';
  return {
    alignItems: 'center',
    appearance: 'none',
    background: resolveActionStyleValue(isStop, disabled, {
      stop: 'var(--flare-accent)',
      disabled: 'var(--flare-surface-bg-active)',
      active: 'var(--flare-accent)',
    }),
    border: 0,
    borderRadius: 999,
    boxShadow: 'none',
    boxSizing: 'border-box',
    color: resolveActionStyleValue(isStop, disabled, {
      stop: '#ffffff',
      disabled: 'var(--flare-text-secondary)',
      active: '#ffffff',
    }),
    cursor: disabled ? 'default' : 'pointer',
    display: 'inline-flex',
    flex: `0 0 ${SUBMIT_BUTTON_SIZE}px`,
    height: SUBMIT_BUTTON_SIZE,
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: SUBMIT_BUTTON_SIZE,
    outline: 'none',
    padding: 0,
    width: SUBMIT_BUTTON_SIZE,
  };
}

function SendPlaneIcon({ color = 'currentColor' }) {
  return (
    <svg
      aria-hidden="true"
      className="flare-send-button-icon"
      fill="none"
      height="18"
      viewBox="0 0 24 24"
      width="18"
    >
      <path
        d="M4.4 11.1 19.2 4.4c.4-.2.8.2.6.6l-6.7 14.8c-.2.5-.9.4-1-.1l-1.3-6.5-6.5-1.3c-.5-.1-.6-.8-.1-1Z"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.9"
      />
      <path
        d="m10.9 13.1 3.5-3.5"
        stroke={color}
        strokeLinecap="round"
        strokeWidth="1.9"
      />
    </svg>
  );
}

function SubmittingIndicator() {
  return (
    <span className="flare-composer-loading-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

export function resolveComposerPrimaryActionState({
  disabled = false,
  loading = false,
  submitting = false,
  sendDisabled = false,
  hasAbort = false,
}) {
  if (loading && hasAbort) {
    return { action: 'stop', disabled: false };
  }
  return {
    action: 'send',
    disabled: resolveSendDisabledState({ disabled, loading, submitting, sendDisabled }),
  };
}

const SUBMIT_ACTION_DEFAULTS = Object.freeze({
  disabled: false,
  loading: false,
  submitting: false,
  sendDisabled: false,
  sendLabel: '发送',
  stopLabel: '停止',
});

export default function ComposerSubmitAction(props) {
  const {
  disabled,
  loading,
  submitting,
  sendDisabled,
  sendLabel,
  stopLabel,
  onSend,
  onAbort,
  } = { ...SUBMIT_ACTION_DEFAULTS, ...props };
  const actionState = resolveComposerPrimaryActionState({
    disabled,
    loading,
    submitting,
    sendDisabled,
    hasAbort: typeof onAbort === 'function',
  });
  const buttonStyle = resolveComposerSubmitButtonStyle({
    action: actionState.action,
    disabled: actionState.disabled,
  });

  if (actionState.action === 'stop') {
    return (
      <button
        aria-label={`${stopLabel}生成`}
        className="flare-composer-submit-button flare-stop-button"
        onClick={onAbort}
        style={buttonStyle}
        title={`${stopLabel}生成`}
        type="button"
      >
        <span
          aria-hidden="true"
          className="flare-stop-button-icon"
          style={{
            background: 'currentColor',
            borderRadius: 2.5,
            display: 'inline-block',
            height: 10,
            width: 10,
          }}
        />
      </button>
    );
  }

  return (
    <button
      aria-busy={submitting}
      aria-label={submitting ? '发送中' : sendLabel}
      className="flare-composer-submit-button flare-send-button"
      disabled={actionState.disabled}
      onClick={onSend}
      style={buttonStyle}
      title={submitting ? '发送中' : `${sendLabel} (Enter)`}
      type="button"
    >
      {submitting ? <SubmittingIndicator /> : <SendPlaneIcon />}
    </button>
  );
}
